/**
 * React Query Default Options
 * Global configuration for queries and mutations
 * 
 * @task FND-FE-002: State Management Foundation
 */

import type { DefaultOptions, QueryCache, MutationCache } from '@tanstack/react-query';
import { errorReporting } from '@/services/errorReporting';

/**
 * Global error handler for queries
 */
function handleQueryError(error: unknown, query: any): void {
  console.error('Query error:', error);
  
  errorReporting.captureException(error, {
    tags: { type: 'query_error', queryKey: query.queryKey.join('/') },
    extra: {
      queryKey: query.queryKey,
      queryHash: query.queryHash,
    },
  });
}

/**
 * Global error handler for mutations
 */
function handleMutationError(error: unknown, variables: unknown, context: unknown): void {
  console.error('Mutation error:', error);
  
  errorReporting.captureException(error, {
    tags: { type: 'mutation_error' },
    extra: { variables, context },
  });
}

/**
 * Default query options optimized for the application
 */
export const defaultQueryOptions: DefaultOptions = {
  queries: {
    // Stale time - data is considered fresh for 1 minute
    staleTime: 60 * 1000,
    
    // Cache time - unused data is kept in cache for 5 minutes
    gcTime: 5 * 60 * 1000,
    
    // Retry configuration
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors (client errors)
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      // Retry up to 3 times for other errors
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Refetch configuration
    refetchOnWindowFocus: false, // Disable to prevent excessive refetching
    refetchOnReconnect: true,    // Refetch when network reconnects
    refetchOnMount: 'always',    // Refetch when component mounts
    
    // Network configuration
    networkMode: 'online',       // Only run queries when online
    
    // Error handling
    throwOnError: false,         // Don't throw errors, let components handle them
    
    // Performance
    notifyOnChangeProps: 'all',  // Optimize re-renders
  },
  
  mutations: {
    // Retry configuration
    retry: 1,                    // Retry once for mutations
    retryDelay: 1000,
    
    // Network configuration
    networkMode: 'online',
    
    // Error handling
    throwOnError: false,
  },
};

/**
 * Query cache configuration
 */
export const queryCacheConfig = {
  onError: handleQueryError,
};

/**
 * Mutation cache configuration
 */
export const mutationCacheConfig = {
  onError: handleMutationError,
};

/**
 * Optimistic update helper
 * Updates cache immediately while mutation is in flight
 */
export function optimisticUpdate<TData, TVariables>(
  queryKey: string[],
  updater: (oldData: TData | undefined, variables: TVariables) => TData
) {
  return {
    onMutate: async (variables: TVariables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData<TData>(queryKey);
      
      // Optimistically update
      queryClient.setQueryData<TData>(queryKey, (old) => updater(old, variables));
      
      // Return context with snapshot
      return { previousData };
    },
    
    onError: (err: unknown, variables: TVariables, context: any) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey });
    },
  };
}

// Import for optimisticUpdate
import { queryClient } from './queryClient';

export default defaultQueryOptions;
